import type {
    ClassicalAPIResponseAction,
    ClassicalSection,
    ClassicalShelfItem,
    ListAction,
} from '@jet-app/music-classical/api/types';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { ContentKind, fixArtwork, type Link } from '../../models';
import type { Opt } from '@jet/environment/types/optional';
import {
    uniqueItemID,
    uniqueSectionID,
} from '@jet-app/music-classical/utilities/id-provider';
import type {
    TrackLockup,
    TrackLockupLayoutStyle,
} from '@jet-app/music-classical/models/section/track-lockup/TrackLockup';
import type { TrackLockupCollection } from '@jet-app/music-classical/models/section/track-lockup/TrackLockupCollection';
import { createFlowActionForAction } from '../create-flow-action';
import { playActionForClassicalPlayAction } from '@jet-app/music-classical/action-model/PlayAction';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { getSubtitleLink } from './shelf';

export function transformToTrackListSection(
    section: ClassicalSection,
    objectGraph: ObjectGraph,
    screenType: string, // TODO: Create enum for screenType?
    screenId: string,
    prominentItemIdentifier?: string,
): TrackLockupCollection[] {
    // TODO: See if track lists will ever have more than one component as we may need to update this
    const chunkedTrackLists = groupTrackListSections(
        section.components?.[0]?.items,
    );

    return chunkedTrackLists.map((trackGroup, index) =>
        makeTrackWorksList(
            trackGroup,
            objectGraph,
            screenType,
            screenId,
            index,
            prominentItemIdentifier,
        ),
    );
}

// Break works up into collections
export function groupTrackListSections(
    items: ClassicalShelfItem[],
): Array<ClassicalShelfItem[]> {
    return items.reduce((acc, item, idx) => {
        const isWorkHeader = item?.type === 'subheading' || false;

        // Start a new chunk when on first item or when
        // we encounter a subheading item
        if (idx === 0 || isWorkHeader) {
            acc.push([]);
        }

        const currentChunkIdx = acc.length - 1;
        acc[currentChunkIdx].push(item);
        return acc;
    }, []);
}

export function getScreenIdForTrackCollection(
    groupIdx: number,
    screenId: Opt<string>,
    currentWork: Opt<string>,
): string {
    const idParts = [`track-list-${groupIdx}`];

    if (screenId) {
        idParts.push(` - ${screenId}`);
    }
    if (currentWork) {
        idParts.push(` - ${currentWork}`);
    }

    return idParts.join('');
}

export function makeTrackCollection(
    objectGraph: ObjectGraph,
    currentDuration: number,
    screenId: string,
    groupIdx: number,
    subheading: ClassicalShelfItem,
    currentWork?: string,
): TrackLockupCollection {
    const idString = getScreenIdForTrackCollection(
        groupIdx,
        screenId,
        currentWork,
    );
    const composerName = subheading?.action?.header?.composerName;
    const firstRowLinks = [];
    const secondRowLinks = [];

    if (subheading.action?.header?.composerAction) {
        const action = createFlowActionForAction(
            subheading.action.header.composerAction,
            objectGraph,
        );

        if (action) {
            firstRowLinks.push({
                title: composerName,
                segue: action,
            });
        }
    }

    if (subheading.contextMenuAction?.previewAction) {
        const action = createFlowActionForAction(
            subheading.contextMenuAction.previewAction,
            objectGraph,
        );

        if (action) {
            secondRowLinks.push({
                title: currentWork,
                segue: action,
            });
        }
    }

    let firstRowText = composerName ?? '';
    let secondRowText = currentWork ?? '';

    // If there is not enough data to display across two lines, then render
    // the currentWork on the first line, styled the same as a composer name.
    if (!firstRowLinks.length && !secondRowLinks.length && !composerName) {
        firstRowText = currentWork;
        secondRowText = '';
    }

    return {
        // Adding index to ensure uniqueness of the work header id.  There maybe be
        // multiple tracks on the same album that are from the same Work, but not
        // grouped together.
        id: uniqueSectionID(idString, objectGraph),
        itemKind: 'trackLockup',
        presentation: {
            kind: 'collection',
            layout: 'list',
        },
        items: [],
        ...(firstRowText || secondRowText
            ? {
                  header: {
                      kind: ContentKind.Work,
                      item: {
                          firstRowText,
                          secondRowText,
                          firstRowLinks,
                          secondRowLinks,
                          duration: currentDuration,
                          groupingIdentifier: screenId,
                          ...(subheading.contextMenuAction
                              ? {
                                    contextMenuAction:
                                        makeContextualMenuFetchAction(
                                            subheading.contextMenuAction?.url,
                                        ),
                                }
                              : {}),
                      },
                  },
              }
            : {}),
    };
}

export function makeTrackCollectionItem(
    track: ClassicalShelfItem,
    objectGraph: ObjectGraph,
    layoutStyle: TrackLockupLayoutStyle,
    screenId: string = '',
    currentWork: string = '',
    groupIdx?: number,
    prominentItemIdentifier?: string,
): TrackLockup {
    return {
        id: uniqueItemID(
            `track-lockup - ${screenId || groupIdx}`, // some collections may not have a screen id
            (!!track.inLibrary || track.payload?.type === 'library-songs') &&
                track.payload?.id
                ? `${track.id} - ${track.payload?.id}`
                : track.id,
            objectGraph,
        ),
        artistName: track.subtitle, // artistName is used for aria-label on track lists
        artwork: fixArtwork({
            url: track.image?.url ?? track.contextMenuAction?.previewImage?.url,
        }),
        composer: track.workSubheading?.action?.header?.composerName,
        duration: track.durationMs,
        isDisabled: track.action?.playParams?.length === 0,
        isPreviewMode: false, // TODO: Add logic to determine if this is preview mode or not
        isProminent: track.id === prominentItemIdentifier,
        isSectionGrouped: true,
        layoutStyle,
        playAction: playActionForClassicalPlayAction(
            track.action as unknown as ClassicalAPIResponseAction,
        ),
        // previewUrl, // TODO: previewUrl?
        showExplicitBadge: false, // TODO: They do exist!  Try to find an example.
        showPopularityIndicator: false, // TODO: Does Classical support popular indicator?
        subtitleLinks: Array.isArray(track.subtitleActions)
            ? track.subtitleActions
                  .map((action) => getSubtitleLink(action, objectGraph))
                  .filter(Boolean)
            : [],
        subtitle: track.subtitle,
        tertiaryLinks: null, // TODO: Find an instnace where this would apply
        title: track.title,
        type: ContentKind.Song, // Type property from API is "track" but making "song" for consistencty with Music
        // trackNumber, // TODO: See if this would even apply
        workName: currentWork,
        contextMenuAction: track.contextMenuAction
            ? makeContextualMenuFetchAction(
                  track.contextMenuAction.url.replace(
                      'renderUserComponentsForTrack=false',
                      'renderUserComponentsForTrack=true',
                  ), // Todo: ask Classical API to fix this for web, and update this parameter
              )
            : null,
    };
}

// Currently every album and playlist track will be part of a work
// TODO: Create Composer flow action from track.action.header.composerAction
// TODO: Create Work flow action for clickable link in Work header
// TODO: Create Work play action for the entire work.  Currently not supported
//       by the API.  When hovering over the work header, a play button will
//       display next to it.  Clicking it will play the entire work.
//
// This function takes a Work collection and returns a track list section
// Since the Work groups are already grouped together, we can make assumptions,
// such as the first item always being the subheading
export function makeTrackWorksList(
    items: ClassicalShelfItem[],
    objectGraph: ObjectGraph,
    screenType: string,
    screenId: string,
    groupIdx: number,
    prominentItemIdentifier?: string,
): TrackLockupCollection {
    let trackCollection: TrackLockupCollection;
    let currentWork: Opt<string>;
    let currentDuration: number = 0;

    items.forEach((track) => {
        if (track.type === 'subheading') {
            const workTitle = track.action?.header?.title ?? track.title;
            const workSubtitle = track.action?.header?.subtitle ?? null;

            // TODO: Just use workTitle once API changes are made per https://quip-apple.com/OEisAabwhRte
            //       Direction was to use main title/subtitle properties from the main subheading object
            //       and file any fomratting radars to API team to adjust for Web platform.
            currentWork = !!workSubtitle
                ? `${workTitle}, ${workSubtitle}`
                : workTitle;
            trackCollection = makeTrackCollection(
                objectGraph,
                currentDuration,
                screenId,
                groupIdx,
                track,
                currentWork,
            );
        } else {
            let layoutStyle: TrackLockupLayoutStyle;
            if (screenType === 'album') {
                layoutStyle = {
                    kind: 'albumTrackList',
                    hasVideo: false, // TODO: Video support?
                };
            } else if (screenType === 'recording') {
                layoutStyle = {
                    kind: 'recordingTrackList',
                    hasVideo: false, // TODO: Video support?
                };
            } else {
                layoutStyle = {
                    kind: 'playlistTrackList',
                    hasVideo: false, // TODO: Video support?
                    hasBadging: false,
                };
            }

            // Add duration of this track to the running work header total
            currentDuration = currentDuration += track.durationMs ?? 0;

            // Tracks on recording pages may not have a work header assocaited with it
            // In this case, we need to make a new track collection for the track.
            if (!trackCollection) {
                trackCollection = makeTrackCollection(
                    objectGraph,
                    currentDuration,
                    screenId,
                    groupIdx,
                    track,
                    currentWork,
                );
            }

            trackCollection.items.push(
                makeTrackCollectionItem(
                    track,
                    objectGraph,
                    layoutStyle,
                    screenId,
                    currentWork,
                    groupIdx,
                    prominentItemIdentifier,
                ),
            );
        }
    });

    // Using dot notation for duration results in TS getting confused
    if (trackCollection.header) {
        trackCollection.header.item['duration'] = currentDuration;
    }

    return trackCollection;
}
